import React from "react";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";
import css from "./DataTable.module.css";
import EmptyState from "./EmptyState";

/**
 * Tabla de datos unificada para el panel admin.
 *
 * Props:
 * - columns: [{ key, label, align?, sortable?, render? }]
 *     - key (string): campo del objeto data
 *     - label (string): texto del encabezado
 *     - align ("left" | "right" | "center"): alineación (default: left)
 *     - sortable (boolean): permite ordenar por esta columna
 *     - render (function(value, row, index)): render custom de la celda
 *     - width (string): ancho CSS de la columna (ej: "120px", "1fr")
 * - data: array de objetos
 * - sortKey (string, opcional): columna actualmente ordenada
 * - sortDirection ("asc" | "desc", opcional): dirección del sort
 * - onSort (function(key), opcional): callback al click en header sortable
 * - emptyIcon (Component, opcional): icono para empty state
 * - emptyTitle (string, opcional): título del empty state
 * - emptyDescription (string, opcional): descripción del empty state
 * - emptyAction (object, opcional): acción del empty state
 * - onRowClick (function(row, index), opcional): click en fila
 * - footer (ReactNode, opcional): contenido del tfoot
 * - maxHeight (string, opcional): altura máxima con scroll (ej: "400px")
 * - dense (boolean, opcional): celdas compactas (tablas editables con muchas columnas)
 * - stickyFirstColumn (boolean, opcional): la primera columna queda fija al scrollear en horizontal
 */
const DataTable = ({
  columns = [],
  data = [],
  sortKey,
  sortDirection,
  onSort,
  emptyIcon,
  emptyTitle = "No hay datos",
  emptyDescription,
  emptyAction,
  onRowClick,
  footer,
  maxHeight,
  dense = false,
  stickyFirstColumn = false,
}) => {
  if (data.length === 0) {
    return (
      <div className={css.wrapper}>
        <EmptyState
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      </div>
    );
  }

  const renderSortIcon = (col) => {
    if (!col.sortable) return null;
    if (sortKey === col.key) {
      return sortDirection === "asc" ? (
        <FaSortUp className={`${css.sortIcon} ${css.sortIconActive}`} />
      ) : (
        <FaSortDown className={`${css.sortIcon} ${css.sortIconActive}`} />
      );
    }
    return <FaSort className={css.sortIcon} />;
  };

  return (
    <div
      className={css.wrapper}
      style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}
    >
      <table
        className={[css.table, dense ? css.dense : "", stickyFirstColumn ? css.stickyFirst : ""].filter(Boolean).join(" ")}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${css.th} ${col.sortable ? css.thSortable : ""}`}
                style={{
                  textAlign: col.align || "left",
                  width: col.width || undefined,
                }}
                onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                aria-sort={
                  col.sortable && sortKey === col.key
                    ? sortDirection === "asc"
                      ? "ascending"
                      : "descending"
                    : undefined
                }
              >
                {col.label}
                {renderSortIcon(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={row.id || rowIndex}
              className={css.tr}
              onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
              style={onRowClick ? { cursor: "pointer" } : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`${css.td} ${col.align === "right" ? css.tdRight : ""}`}
                  style={{ textAlign: col.align || "left" }}
                >
                  {col.render
                    ? col.render(row[col.key], row, rowIndex)
                    : row[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot className={css.tfoot}>{footer}</tfoot>}
      </table>
    </div>
  );
};

export default DataTable;
